import type { FormField } from "@tellescope/types-models";
import { advanceWhile, type FieldPathWalkContext } from "./walk-field-path";
import { getNextField } from "./get-next-field";
import { occupiesOwnPage } from "./question-group";
import type { FormFieldNode } from "./types";

export interface NodesToPush {
  /** Every node crossed on the way to the next page, in path order, the page itself last when reached */
  nodes: FormFieldNode[];
  /**
   * False when the path ends before another page: `nodes` is then the trailing pass-through run,
   * which the wizard does not push — the form is on its last page.
   */
  reachedPage: boolean;
}

export interface ResolveNodesToPushArgs {
  activeField: FormFieldNode;
  /** All fields by id, used to resolve Question Group sub-field types */
  fieldsMap: Record<string, FormField | undefined>;
  /** Ids belonging to a Question Group, which render inside their parent rather than as a page */
  subFieldIds: ReadonlySet<string>;
  walkContext: FieldPathWalkContext;
}

/**
 * What Next pushes onto the stack: the run of nodes with no page of their own (`Hidden Value`, a
 * group made only of them) between the active field and the next field that renders, then that
 * field.
 */
export const resolveNodesToPush = ({
  activeField,
  fieldsMap,
  subFieldIds,
  walkContext,
}: ResolveNodesToPushArgs): NodesToPush => {
  const first = getNextField(
    activeField,
    walkContext.responsesByFieldId[activeField.value.id],
    walkContext.allResponses,
    walkContext.logicOptions
  );
  if (!first) return { nodes: [], reachedPage: false };

  const { node, skipped } = advanceWhile(
    first,
    (candidate) => !occupiesOwnPage(candidate.value, subFieldIds, fieldsMap),
    walkContext
  );

  const reachedPage = occupiesOwnPage(node.value, subFieldIds, fieldsMap);
  return { nodes: [...skipped, node], reachedPage };
};
